import { create } from 'zustand'
import { ExpandedRatingOptions, RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from '../deal-builder/services/utils/primary-constants.ts';
import moment, { Moment } from 'moment';

export interface DealBuilderInputStore {
  // Report titles
  borrower: string,
  setBorrower: (borrower: string) => void,
  issuanceTitle: string,
  setIssuanceTitle: (issuanceTitle: string) => void,

  // Pricing assumptions
  marketConditionsDate: Moment,
  setMarketConditionsDate: (marketConditionsDate: Moment) => void,
  stateOfIssuance: USState,
  setStateOfIssuance: (stateOfIssuance: USState) => void,
  securityType: SecurityType,
  setSecurityType: (securityType: SecurityType) => void,
  ratingAssumption: RatingAssumption,
  setRatingAssumption: (ratingAssumption: RatingAssumption) => void,
  taxDesignation: TaxDesignation,
  setTaxDesignation: (taxDesignation: TaxDesignation) => void,
  saleMethod: SaleMethod,
  setSaleMethod: (saleMethod: SaleMethod) => void,
  useStateEnhancement: boolean,
  setUseStateEnhancement: (setUseStateEnhancement: boolean) => void,
  stateEnhancement: ExpandedRatingOptions | undefined,
  setStateEnhancement: (stateEnhancement: ExpandedRatingOptions | undefined) => void,
  bondInsurance: boolean,
  setBondInsurance: (bondInsurance: boolean) => void,

  // Amortization assumptions
  parAmount: number,
  setParAmount: (parAmount: number) => void,
  denomination: number;
  setDenomination: (denomination: number) => void,
  deliveryDate: Moment;
  setDeliveryDate: (deliveryDate: Moment) => void,
  firstCouponDate: Moment;
  setFirstCouponDate: (firstCouponDate: Moment) => void,
  firstPrincipalDate: Moment;
  setFirstPrincipalDate: (firstPrincipalDate: Moment) => void,
  finalPrincipalDate: Moment;
  setFinalPrincipalDate: (finalPrincipalDate: Moment) => void,
  principalRate: number,
  setPrincipalRate: (principalRate: number) => void,
  useCallDate: boolean,
  setUseCallDate: (useCallDate: boolean) => void,
  callDate: Moment,
  setCallDate: (callDate: Moment) => void,

  // Term bonds
  termBonds: [Moment, Moment][],
  setTermBonds: (termBonds: [Moment, Moment][]) => void,

  // Meta
  toggledSections: string[],
  setToggledSections: (toggledSections: string[]) => void,

  // Update all the parameters
  updateAll: (data: DealBuilderInputStore) => void,
}

function getDefaultCouponDate(settlement: Moment): Moment {
  const date = moment("12/01", "MM/DD").year(moment().year() + (moment().month() === 11 ? 1 : 0));
  if (date.isSameOrBefore(settlement)) {
    date.add(6, "months");
  }
  return date;
}

// Report titles
const DEFAULT_BORROWER = "";
const DEFAULT_ISSUANCE_TITLE = "";

// Pricing assumptions
const DEFAULT_MARKET_CONDITIONS_DATE = moment();
const DEFAULT_STATE = "Alabama" as USState;
const DEFAULT_SECURITY_TYPE = SecurityType.UnlimitedTaxGeneralObligation;
const DEFAULT_RATING_ASUMPTION = RatingAssumption.AA;
const DEFAULT_TAX_DESIGNATION = TaxDesignation.NonBankQualified;
const DEFAULT_SALE_METHOD = SaleMethod.Negotiated;
const DEFAULT_USE_STATE_ENHANCEMENT = false;
const DEFAULT_STATE_ENHANCEMENT = undefined;
const DEFAULT_BOND_INSURANCE = false;

// Amortization assumptions
const DEFAULT_PAR_AMOUNT = 10_000_000;
const DEFAULT_DENOMINATION = 5000;
const DEFAULT_SETTLEMENT_DATE = moment().add(30, "days");
const DEFAULT_FIRST_COUPON_DATE = getDefaultCouponDate(DEFAULT_SETTLEMENT_DATE);
const DEFAULT_FIRST_PRINCIPAL_DATE = moment().add(90, "days").month(11).date(1);
const DEFAULT_FINAL_PRINCIPAL_DATE = moment("12/01", "MM/DD").year(moment().year() + 19);
const DEFAULT_PRINCIPAL_FREQUENCY = 1;
export const DEFAULT_CALL_DATE = moment("12/01", "MM/DD").year(moment().year() + 8);
const DEFAULT_USE_CALL_DATE = true;

// Term bonds
const DEFAULT_TERM_BONDS = [];

// Meta
const DEFAULT_TOGGLED_SECTIONS = [];

const useDealBuilderInputsStore = create<DealBuilderInputStore>((set) => ({
  // Report titles
  borrower: DEFAULT_BORROWER,
  setBorrower: (borrower: string) => set({ borrower }),
  issuanceTitle: DEFAULT_ISSUANCE_TITLE,
  setIssuanceTitle: (issuanceTitle: string) => set({ issuanceTitle }),

  // Pricing assumptions
  marketConditionsDate: DEFAULT_MARKET_CONDITIONS_DATE,
  setMarketConditionsDate: (marketConditionsDate: Moment) => set({ marketConditionsDate }),
  stateOfIssuance: DEFAULT_STATE,
  setStateOfIssuance: (stateOfIssuance: USState) => set({ stateOfIssuance }),
  securityType: DEFAULT_SECURITY_TYPE,
  setSecurityType: (securityType: SecurityType) => set({ securityType }),
  ratingAssumption: DEFAULT_RATING_ASUMPTION,
  setRatingAssumption: (ratingAssumption: RatingAssumption) => set({ ratingAssumption }),
  taxDesignation: DEFAULT_TAX_DESIGNATION,
  setTaxDesignation: (taxDesignation: TaxDesignation) => set({ taxDesignation }),
  saleMethod: DEFAULT_SALE_METHOD,
  setSaleMethod: (saleMethod: SaleMethod) => set({ saleMethod }),
  useStateEnhancement: DEFAULT_USE_STATE_ENHANCEMENT,
  setUseStateEnhancement: (useStateEnhancement: boolean) => set({ useStateEnhancement }),
  stateEnhancement: DEFAULT_STATE_ENHANCEMENT,
  setStateEnhancement: (stateEnhancement: ExpandedRatingOptions | undefined) => set({ stateEnhancement }),
  bondInsurance: DEFAULT_BOND_INSURANCE,
  setBondInsurance: (bondInsurance: boolean) => set({ bondInsurance }),

  // Amortization assumptions
  parAmount: DEFAULT_PAR_AMOUNT,
  setParAmount: (parAmount: number) => set({ parAmount }),
  denomination: DEFAULT_DENOMINATION,
  setDenomination: (denomination: number) => set({ denomination }),
  deliveryDate: DEFAULT_SETTLEMENT_DATE,
  setDeliveryDate: (deliveryDate: Moment) => set({ deliveryDate }),
  firstCouponDate: DEFAULT_FIRST_COUPON_DATE,
  setFirstCouponDate: (firstCouponDate: Moment) => set({ firstCouponDate }),
  firstPrincipalDate: DEFAULT_FIRST_PRINCIPAL_DATE,
  setFirstPrincipalDate: (firstPrincipalDate: Moment) => set({ firstPrincipalDate }),
  finalPrincipalDate: DEFAULT_FINAL_PRINCIPAL_DATE,
  setFinalPrincipalDate: (finalPrincipalDate: Moment) => set({ finalPrincipalDate }),
  principalRate: DEFAULT_PRINCIPAL_FREQUENCY,
  setPrincipalRate: (principalRate: number) => set({ principalRate }),
  useCallDate: DEFAULT_USE_CALL_DATE,
  setUseCallDate: (useCallDate: boolean) => set({ useCallDate }),
  callDate: DEFAULT_CALL_DATE,
  setCallDate: (callDate: Moment) => set({ callDate }),

  // Term Bonds
  termBonds: DEFAULT_TERM_BONDS,
  setTermBonds: (termBonds: [Moment, Moment][]) => set({ termBonds }),

  // Meta
  toggledSections: DEFAULT_TOGGLED_SECTIONS,
  setToggledSections: (toggledSections: string[]) => set({ toggledSections }),

  // Update all the parameters
  updateAll: (data: DealBuilderInputStore) => set(data),
}));

export default useDealBuilderInputsStore;
